// Single source of truth for the in-app "Help & training" articles.
// Each article is authored as a Markdown file and imported at build time via
// Vite's native `?raw` suffix — no runtime fetch and no backend.

import gettingStarted from './getting-started.md?raw'
import companyProfile from './company-profile.md?raw'
import payrollDefaults from './payroll-defaults.md?raw'
import employees from './employees.md?raw'
import addEmployee from './add-employee.md?raw'
import pendingPayments from './pending-payments.md?raw'
import completedPayments from './completed-payments.md?raw'
import generatePayrun from './generate-payrun.md?raw'
import importPayrun from './import-payrun.md?raw'
import billing from './billing.md?raw'
import notifications from './notifications.md?raw'
import employeeSettings from './employee-settings.md?raw'
import integrations from './integrations.md?raw'
import taxData from './tax-data.md?raw'

export interface HelpArticle {
  /** Route param, e.g. 'import-payrun'. */
  slug: string
  /** Shown in the index list and as the article <h1>. */
  title: string
  /** Grouping key in the index. */
  category: string
  /** One-line card summary. */
  description: string
  /** ISO date (YYYY-MM-DD), rendered as "Updated …". */
  updatedAt: string
  /** Dashboard route(s) this article documents — drives the UI↔docs integrity test. */
  routes: string[]
  /** Raw Markdown body. */
  content: string
}

export const helpArticles: HelpArticle[] = [
  {
    slug: 'getting-started',
    title: 'Getting started & the 90-day trial',
    category: 'Getting started',
    description: 'How PeerPass works and what happens during your 90-day free trial.',
    updatedAt: '2026-09-14',
    routes: [],
    content: gettingStarted,
  },
  {
    slug: 'company-profile',
    title: 'Company profile & limits',
    category: 'Organization setup',
    description: 'Set your company currency, maximum pot amount, and signatory.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/company-profile'],
    content: companyProfile,
  },
  {
    slug: 'payroll-defaults',
    title: 'Payroll defaults',
    category: 'Organization setup',
    description: 'Configure the payment cycle and default limits across your organisation.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/payroll-defaults'],
    content: payrollDefaults,
  },
  {
    slug: 'employees',
    title: 'Managing your employee roster & roles',
    category: 'People',
    description: 'Manage your employee roster, roles, and pseudonyms.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/employees'],
    content: employees,
  },
  {
    slug: 'add-employee',
    title: 'Adding employees (new & existing)',
    category: 'People',
    description: 'Add a new employee or attach an existing PeerPass user.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/add-employee', '/dashboard/add-existing-user'],
    content: addEmployee,
  },
  {
    slug: 'pending-payments',
    title: 'Reviewing pending payments',
    category: 'Payroll',
    description: 'Review and confirm the pay cycles that are due.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/pending-payments'],
    content: pendingPayments,
  },
  {
    slug: 'completed-payments',
    title: 'Completed payments',
    category: 'Payroll',
    description: 'Your historical record of settled cycles.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/completed-payments'],
    content: completedPayments,
  },
  {
    slug: 'generate-payrun',
    title: 'Generate a payrun (CSV export)',
    category: 'Payroll',
    description: 'Export employee or payment data to CSV.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/csv-export'],
    content: generatePayrun,
  },
  {
    slug: 'import-payrun',
    title: 'Import a payrun (CSV import)',
    category: 'Payroll',
    description: 'Upload a payrun CSV and process each payer.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/csv-import'],
    content: importPayrun,
  },
  {
    slug: 'billing',
    title: 'Subscription, invoices & add-ons',
    category: 'Billing',
    description: 'Subscription, invoices, add-ons, and payment instructions.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/billing'],
    content: billing,
  },
  {
    slug: 'notifications',
    title: 'Notifications & preferences',
    category: 'Settings',
    description: 'Choose which alerts you receive and how.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/notifications', '/dashboard/notification-preferences'],
    content: notifications,
  },
  {
    slug: 'employee-settings',
    title: 'Employee Circle settings',
    category: 'Settings',
    description: 'Configure per-employee circle limits and overrides.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/employee-settings'],
    content: employeeSettings,
  },
  {
    slug: 'integrations',
    title: 'Integrations (SSO / HRIS)',
    category: 'Settings',
    description: 'Connect your HRIS or payroll provider and keep data in sync.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/integrations'],
    content: integrations,
  },
  {
    slug: 'tax-data',
    title: 'Tax data',
    category: 'Compliance',
    description: 'Tax records and filing status by year.',
    updatedAt: '2026-09-14',
    routes: ['/dashboard/tax-forms'],
    content: taxData,
  },
]

/** Ordered, de-duplicated categories derived from the article list. */
export const helpCategories: string[] = [
  ...new Set(helpArticles.map((a) => a.category)),
]

export const findArticle = (slug: string): HelpArticle | undefined =>
  helpArticles.find((a) => a.slug === slug)